import { createApi } from '@reduxjs/toolkit/query/react';
import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import { RootState } from '../app/store';
import { setCredentials, tokenReceived } from '../features/auth/authSlice';
import { UserNoPassword, UserProfile } from './api-types';
import configData from '../configData.json';

const baseQuery = fetchBaseQuery({
  baseUrl: configData.SERVER_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
});
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);
  if (result.error && result.error.status === 401) {
    const user = (api.getState() as RootState).auth.user;
    if (user) {
      const { id, refreshToken } = user;
      const refreshResult = await baseQuery(
        { url: '/auth/refresh', method: 'POST', body: { id, refreshToken } },
        api,
        extraOptions
      );
      if (refreshResult.data) {
        // @ts-ignore
        api.dispatch(tokenReceived({ token: refreshResult.data.token }));
        result = await baseQuery(args, api, extraOptions);
      } else {
        api.dispatch(setCredentials({ user: null, token: '' }));
      }
    }
  }
  return result;
};
export const api = createApi({
  baseQuery: baseQueryWithReauth,
  tagTypes: [],
  endpoints: (build) => ({
    getHello: build.query<GetHelloApiResponse, GetHelloApiArg>({
      query: () => ({ url: `/` }),
    }),
    login: build.mutation<LoginApiResponse, LoginApiArg>({
      query: (queryArg) => ({
        url: `/auth/login`,
        method: 'POST',
        body: queryArg,
      }),
    }),
    logout: build.mutation<LogoutApiResponse, LogoutApiArg>({
      query: (queryArg) => ({
        url: `/auth/logout/${queryArg.id}`,
        method: 'PUT',
      }),
    }),
    refresh: build.mutation<RefreshApiResponse, RefreshApiArg>({
      query: (queryArg) => ({
        url: `/auth/refresh`,
        method: 'POST',
        body: queryArg.refreshDto,
      }),
    }),
    createUser: build.mutation<CreateUserApiResponse, CreateUserApiArg>({
      query: (queryArg) => ({
        url: `/users`,
        method: 'POST',
        body: queryArg.createUserDto,
      }),
    }),
    findAllUsers: build.query<FindAllUsersApiResponse, FindAllUsersApiArg>({
      query: () => ({ url: `/users` }),
    }),
    createUserNoHash: build.mutation<
      CreateUserNoHashApiResponse,
      CreateUserNoHashApiArg
    >({
      query: (queryArg) => ({
        url: `/users/no-hash`,
        method: 'POST',
        body: queryArg.createUserDto,
      }),
    }),
    findUserForUser: build.query<
      FindUserForUserApiResponse,
      FindUserForUserApiArg
    >({
      query: () => ({ url: `/users/for-user` }),
    }),
    updateForUser: build.mutation<
      UpdateForUserApiResponse,
      UpdateForUserApiArg
    >({
      query: (queryArg) => ({
        url: `/users/for-user`,
        method: 'PUT',
        body: queryArg.updateUserDto,
      }),
    }),
    findAllUsersForUser: build.query<
      FindAllUsersForUserApiResponse,
      FindAllUsersForUserApiArg
    >({
      query: () => ({ url: `/users/for-user-all` }),
    }),
    findOneUser: build.query<FindOneUserApiResponse, FindOneUserApiArg>({
      query: (queryArg) => ({ url: `/users/${queryArg.id}` }),
    }),
    updateUser: build.mutation<UpdateUserApiResponse, UpdateUserApiArg>({
      query: (queryArg) => ({
        url: `/users/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.updateUserDto,
      }),
    }),
    removeUser: build.mutation<RemoveUserApiResponse, RemoveUserApiArg>({
      query: (queryArg) => ({ url: `/users/${queryArg.id}`, method: 'DELETE' }),
    }),
    updateEmailPasswordForUser: build.mutation<
      UpdateEmailPasswordForUserApiResponse,
      UpdateEmailPasswordForUserApiArg
    >({
      query: (queryArg) => ({
        url: `/users/for-user/change-password`,
        method: 'PUT',
        body: queryArg.changePasswordDto,
      }),
    }),
    findAllCompanies: build.query<
      FindAllCompaniesApiResponse,
      FindAllCompaniesApiArg
    >({
      query: () => ({ url: `/companies` }),
    }),
    createCompany: build.mutation<
      CreateCompanyApiResponse,
      CreateCompanyApiArg
    >({
      query: (queryArg) => ({
        url: `/companies`,
        method: 'POST',
        body: queryArg.createCompanyDto,
      }),
    }),
    findOneCompany: build.query<
      FindOneCompanyApiResponse,
      FindOneCompanyApiArg
    >({
      query: (queryArg) => ({ url: `/companies/${queryArg.id}` }),
    }),
    updateCompany: build.mutation<
      UpdateCompanyApiResponse,
      UpdateCompanyApiArg
    >({
      query: (queryArg) => ({
        url: `/companies/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.updateCompanyDto,
      }),
    }),
    deleteCompany: build.mutation<
      DeleteCompanyApiResponse,
      DeleteCompanyApiArg
    >({
      query: (queryArg) => ({
        url: `/companies/${queryArg.id}`,
        method: 'DELETE',
      }),
    }),
    createDemo: build.mutation<CreateDemoApiResponse, CreateDemoApiArg>({
      query: (queryArg) => ({
        url: `/demos`,
        method: 'POST',
        body: queryArg.createDemoDto,
      }),
    }),
    findAllDemos: build.query<FindAllDemosApiResponse, FindAllDemosApiArg>({
      query: () => ({ url: `/demos` }),
    }),
    findAllDemosForUser: build.query<
      FindAllDemosForUserApiResponse,
      FindAllDemosForUserApiArg
    >({
      query: () => ({ url: `/demos/for-user` }),
    }),
    findOneDemos: build.query<FindOneDemosApiResponse, FindOneDemosApiArg>({
      query: (queryArg) => ({ url: `/demos/${queryArg.id}` }),
    }),
    updateDemo: build.mutation<UpdateDemoApiResponse, UpdateDemoApiArg>({
      query: (queryArg) => ({
        url: `/demos/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.updateDemoDto,
      }),
    }),
    removeDemo: build.mutation<RemoveDemoApiResponse, RemoveDemoApiArg>({
      query: (queryArg) => ({ url: `/demos/${queryArg.id}`, method: 'DELETE' }),
    }),
    createSoftware: build.mutation<
      CreateSoftwareApiResponse,
      CreateSoftwareApiArg
    >({
      query: (queryArg) => ({
        url: `/software`,
        method: 'POST',
        body: queryArg.createSoftwareDto,
      }),
    }),
    findAllSoftware: build.query<
      FindAllSoftwareApiResponse,
      FindAllSoftwareApiArg
    >({
      query: () => ({ url: `/software` }),
    }),
    findMostRecentVersionName: build.query<
      FindMostRecentVersionNameApiResponse,
      FindMostRecentVersionNameApiArg
    >({
      query: (queryArg) => ({
        url: `/software/${queryArg.id}/current_version`,
      }),
    }),
    findOneSoftware: build.query<
      FindOneSoftwareApiResponse,
      FindOneSoftwareApiArg
    >({
      query: (queryArg) => ({ url: `/software/${queryArg.id}` }),
    }),
    updateSoftware: build.mutation<
      UpdateSoftwareApiResponse,
      UpdateSoftwareApiArg
    >({
      query: (queryArg) => ({
        url: `/software/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.updateSoftwareDto,
      }),
    }),
    removeSoftware: build.mutation<
      RemoveSoftwareApiResponse,
      RemoveSoftwareApiArg
    >({
      query: (queryArg) => ({
        url: `/software/${queryArg.id}`,
        method: 'DELETE',
      }),
    }),
    createSoftwareVersion: build.mutation<
      CreateSoftwareVersionApiResponse,
      CreateSoftwareVersionApiArg
    >({
      query: (queryArg) => ({
        url: `/software-versions`,
        method: 'POST',
        body: queryArg.createSoftwareVersionDto,
      }),
    }),
    findAllSoftwareVersions: build.query<
      FindAllSoftwareVersionsApiResponse,
      FindAllSoftwareVersionsApiArg
    >({
      query: () => ({ url: `/software-versions` }),
    }),
    findAllSoftwareVersionsForUser: build.query<
      FindAllSoftwareVersionsForUserApiResponse,
      FindAllSoftwareVersionsForUserApiArg
    >({
      query: () => ({ url: `/software-versions/for-user` }),
    }),
    findOneSoftwareVersion: build.query<
      FindOneSoftwareVersionApiResponse,
      FindOneSoftwareVersionApiArg
    >({
      query: (queryArg) => ({ url: `/software-versions/${queryArg.id}` }),
    }),
    updateSoftwareVersion: build.mutation<
      UpdateSoftwareVersionApiResponse,
      UpdateSoftwareVersionApiArg
    >({
      query: (queryArg) => ({
        url: `/software-versions/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.updateSoftwareVersionDto,
      }),
    }),
    removeSoftwareVersion: build.mutation<
      RemoveSoftwareVersionApiResponse,
      RemoveSoftwareVersionApiArg
    >({
      query: (queryArg) => ({
        url: `/software-versions/${queryArg.id}`,
        method: 'DELETE',
      }),
    }),
    findLatestSoftwareVersion: build.query<
      FindLatestSoftwareVersionApiResponse,
      FindLatestSoftwareVersionApiArg
    >({
      query: (queryArg) => ({
        url: `/software-versions/current/${queryArg.id}`,
      }),
    }),
    sendReleaseEmail: build.mutation<
      SendReleaseEmailApiResponse,
      SendReleaseEmailApiArg
    >({
      query: (queryArg) => ({
        url: `/software-versions/release-email`,
        method: 'POST',
        body: queryArg.sendVersionReleaseEmailDto,
      }),
    }),
    createOperatingSystem: build.mutation<
      CreateOperatingSystemApiResponse,
      CreateOperatingSystemApiArg
    >({
      query: (queryArg) => ({
        url: `/operating-systems`,
        method: 'POST',
        body: queryArg.createOperatingSystemDto,
      }),
    }),
    findAllOperatingSystems: build.query<
      FindAllOperatingSystemsApiResponse,
      FindAllOperatingSystemsApiArg
    >({
      query: () => ({ url: `/operating-systems` }),
    }),
    findOneOperatingSystem: build.query<
      FindOneOperatingSystemApiResponse,
      FindOneOperatingSystemApiArg
    >({
      query: (queryArg) => ({ url: `/operating-systems/${queryArg.id}` }),
    }),
    updateOperatingSystem: build.mutation<
      UpdateOperatingSystemApiResponse,
      UpdateOperatingSystemApiArg
    >({
      query: (queryArg) => ({
        url: `/operating-systems/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.updateOperatingSystemDto,
      }),
    }),
    removeOperatingSystem: build.mutation<
      RemoveOperatingSystemApiResponse,
      RemoveOperatingSystemApiArg
    >({
      query: (queryArg) => ({
        url: `/operating-systems/${queryArg.id}`,
        method: 'DELETE',
      }),
    }),
    createLicense: build.mutation<
      CreateLicenseApiResponse,
      CreateLicenseApiArg
    >({
      query: (queryArg) => ({
        url: `/licenses`,
        method: 'POST',
        body: queryArg.createLicenseDto,
      }),
    }),
    findAllLicenses: build.query<
      FindAllLicensesApiResponse,
      FindAllLicensesApiArg
    >({
      query: () => ({ url: `/licenses` }),
    }),
    findAllLicensesByCompanyId: build.query<
      FindAllLicensesByCompanyIdApiResponse,
      FindAllLicensesByCompanyIdApiArg
    >({
      query: (queryArg) => ({ url: `/licenses/company/${queryArg.id}` }),
    }),
    findLicensesForUser: build.query<
      FindLicensesForUserApiResponse,
      FindLicensesForUserApiArg
    >({
      query: () => ({ url: `/licenses/for-user` }),
    }),
    findOneLicense: build.query<
      FindOneLicenseApiResponse,
      FindOneLicenseApiArg
    >({
      query: (queryArg) => ({ url: `/licenses/${queryArg.id}` }),
    }),
    updateLicense: build.mutation<
      UpdateLicenseApiResponse,
      UpdateLicenseApiArg
    >({
      query: (queryArg) => ({
        url: `/licenses/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.updateLicenseDto,
      }),
    }),
    removeLicense: build.mutation<
      RemoveLicenseApiResponse,
      RemoveLicenseApiArg
    >({
      query: (queryArg) => ({
        url: `/licenses/${queryArg.id}`,
        method: 'DELETE',
      }),
    }),
    parseLicenses: build.mutation<
      ParseLicensesApiResponse,
      ParseLicensesApiArg
    >({
      query: (queryArg) => ({
        url: `/licenses/parse`,
        method: 'PUT',
        body: queryArg.parseLicenseDto,
      }),
    }),
    createHardware: build.mutation<
      CreateHardwareApiResponse,
      CreateHardwareApiArg
    >({
      query: (queryArg) => ({
        url: `/hardware`,
        method: 'POST',
        body: queryArg.createHardwareDto,
      }),
    }),
    findAllHardware: build.query<
      FindAllHardwareApiResponse,
      FindAllHardwareApiArg
    >({
      query: () => ({ url: `/hardware` }),
    }),
    findAllHardwareForUser: build.query<
      FindAllHardwareForUserApiResponse,
      FindAllHardwareForUserApiArg
    >({
      query: () => ({ url: `/hardware/for-user` }),
    }),
    findAllDepartmentsForUser: build.query<
      FindAllDepartmentsForUserApiResponse,
      FindAllDepartmentsForUserApiArg
    >({
      query: () => ({ url: `/departments/for-user` }),
    }),
    findOneHardware: build.query<
      FindOneHardwareApiResponse,
      FindOneHardwareApiArg
    >({
      query: (queryArg) => ({ url: `/hardware/${queryArg.id}` }),
    }),
    updateHardware: build.mutation<
      UpdateHardwareApiResponse,
      UpdateHardwareApiArg
    >({
      query: (queryArg) => ({
        url: `/hardware/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.updateHardwareDto,
      }),
    }),
    removeHardware: build.mutation<
      RemoveHardwareApiResponse,
      RemoveHardwareApiArg
    >({
      query: (queryArg) => ({
        url: `/hardware/${queryArg.id}`,
        method: 'DELETE',
      }),
    }),
    createType: build.mutation<CreateTypeApiResponse, CreateTypeApiArg>({
      query: (queryArg) => ({
        url: `/hardware-types`,
        method: 'POST',
        body: queryArg.createHardwareTypeDto,
      }),
    }),
    getAllTypes: build.query<GetAllTypesApiResponse, GetAllTypesApiArg>({
      query: () => ({ url: `/hardware-types` }),
    }),
    getTypeById: build.query<GetTypeByIdApiResponse, GetTypeByIdApiArg>({
      query: (queryArg) => ({ url: `/hardware-types/${queryArg.id}` }),
    }),
    deleteType: build.mutation<DeleteTypeApiResponse, DeleteTypeApiArg>({
      query: (queryArg) => ({
        url: `/hardware-types/${queryArg.id}`,
        method: 'DELETE',
      }),
    }),
    updateType: build.mutation<UpdateTypeApiResponse, UpdateTypeApiArg>({
      query: (queryArg) => ({
        url: `/hardware-types/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.updateHardwareTypeDto,
      }),
    }),
    createHardwareCategory: build.mutation<
      CreateHardwareCategoryApiResponse,
      CreateHardwareCategoryApiArg
    >({
      query: (queryArg) => ({
        url: `/hardware-categories`,
        method: 'POST',
        body: queryArg.createHardwareCategoryDto,
      }),
    }),
    findAllHardwareCategories: build.query<
      FindAllHardwareCategoriesApiResponse,
      FindAllHardwareCategoriesApiArg
    >({
      query: () => ({ url: `/hardware-categories` }),
    }),
    findOneHardwareCategory: build.query<
      FindOneHardwareCategoryApiResponse,
      FindOneHardwareCategoryApiArg
    >({
      query: (queryArg) => ({ url: `/hardware-categories/${queryArg.id}` }),
    }),
    updateHardwareCategory: build.mutation<
      UpdateHardwareCategoryApiResponse,
      UpdateHardwareCategoryApiArg
    >({
      query: (queryArg) => ({
        url: `/hardware-categories/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.updateHardwareCategoryDto,
      }),
    }),
    removeHardwareCategory: build.mutation<
      RemoveHardwareCategoryApiResponse,
      RemoveHardwareCategoryApiArg
    >({
      query: (queryArg) => ({
        url: `/hardware-categories/${queryArg.id}`,
        method: 'DELETE',
      }),
    }),
    createModule: build.mutation<CreateModuleApiResponse, CreateModuleApiArg>({
      query: (queryArg) => ({
        url: `/modules`,
        method: 'POST',
        body: queryArg.createModuleDto,
      }),
    }),
    findAllModules: build.query<
      FindAllModulesApiResponse,
      FindAllModulesApiArg
    >({
      query: () => ({ url: `/modules` }),
    }),
    findOneModule: build.query<FindOneModuleApiResponse, FindOneModuleApiArg>({
      query: (queryArg) => ({ url: `/modules/${queryArg.id}` }),
    }),
    updateModule: build.mutation<UpdateModuleApiResponse, UpdateModuleApiArg>({
      query: (queryArg) => ({
        url: `/modules/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.updateModuleDto,
      }),
    }),
    removeModule: build.mutation<RemoveModuleApiResponse, RemoveModuleApiArg>({
      query: (queryArg) => ({
        url: `/modules/${queryArg.id}`,
        method: 'DELETE',
      }),
    }),
    createOrder: build.mutation<CreateOrderApiResponse, CreateOrderApiArg>({
      query: (queryArg) => ({
        url: `/orders`,
        method: 'POST',
        body: queryArg.createOrderDto,
      }),
    }),
    findAllOrders: build.query<FindAllOrdersApiResponse, FindAllOrdersApiArg>({
      query: () => ({ url: `/orders` }),
    }),
    findOrdersForUser: build.query<
      FindOrdersForUserApiResponse,
      FindOrdersForUserApiArg
    >({
      query: () => ({ url: `/orders/for-user` }),
    }),
    findOneOrder: build.query<FindOneOrderApiResponse, FindOneOrderApiArg>({
      query: (queryArg) => ({ url: `/orders/${queryArg.id}` }),
    }),
    updateOrder: build.mutation<UpdateOrderApiResponse, UpdateOrderApiArg>({
      query: (queryArg) => ({
        url: `/orders/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.updateOrderDto,
      }),
    }),
    removeOrder: build.mutation<RemoveOrderApiResponse, RemoveOrderApiArg>({
      query: (queryArg) => ({
        url: `/orders/${queryArg.id}`,
        method: 'DELETE',
      }),
    }),
    downloadQuote: build.query<DownloadQuoteApiResponse, DownloadQuoteApiArg>({
      query: (queryArg) => ({ url: `/orders/quote/${queryArg.id}` }),
    }),
    downloadPo: build.query<DownloadPoApiResponse, DownloadPoApiArg>({
      query: (queryArg) => ({ url: `/orders/purchase-order/${queryArg.id}` }),
    }),
    uploadPurchaseOrder: build.mutation<
      UploadPurchaseOrderApiResponse,
      UploadPurchaseOrderApiArg
    >({
      query: (body) => ({
        url: `/upload/purchase-order`,
        method: 'POST',
        body,
      }),
    }),
    uploadQuote: build.mutation<UploadQuoteApiResponse, UploadQuoteApiArg>({
      query: (body) => ({ url: `/upload/quote`, method: 'POST', body }),
    }),
    createInventoryHardware: build.mutation<
      CreateInventoryHardwareApiResponse,
      CreateInventoryHardwareApiArg
    >({
      query: (queryArg) => ({
        url: `/inventory-hardware`,
        method: 'POST',
        body: queryArg.createInventoryHardwareDto,
      }),
    }),
    findAllInventoryHardware: build.query<
      FindAllInventoryHardwareApiResponse,
      FindAllInventoryHardwareApiArg
    >({
      query: () => ({ url: `/inventory-hardware` }),
    }),
    findOneInventoryHardware: build.query<
      FindOneInventoryHardwareApiResponse,
      FindOneInventoryHardwareApiArg
    >({
      query: (queryArg) => ({ url: `/inventory-hardware/${queryArg.id}` }),
    }),
    updateInventoryHardware: build.mutation<
      UpdateInventoryHardwareApiResponse,
      UpdateInventoryHardwareApiArg
    >({
      query: (queryArg) => ({
        url: `/inventory-hardware/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.updateInventoryHardwareDto,
      }),
    }),
    removeInventoryHardware: build.mutation<
      RemoveInventoryHardwareApiResponse,
      RemoveInventoryHardwareApiArg
    >({
      query: (queryArg) => ({
        url: `/inventory-hardware/${queryArg.id}`,
        method: 'DELETE',
      }),
    }),
    createFirmware: build.mutation<
      CreateFirmwareApiResponse,
      CreateFirmwareApiArg
    >({
      query: (queryArg) => ({
        url: `/firmware`,
        method: 'POST',
        body: queryArg.createFirmwareDto,
      }),
    }),
    findAllFirmware: build.query<
      FindAllFirmwareApiResponse,
      FindAllFirmwareApiArg
    >({
      query: () => ({ url: `/firmware` }),
    }),
    findAllFirmwareForUser: build.query<
      FindAllFirmwareForUserApiResponse,
      FindAllFirmwareForUserApiArg
    >({
      query: () => ({ url: `/firmware/for-user` }),
    }),
    findOneFirmware: build.query<
      FindOneFirmwareApiResponse,
      FindOneFirmwareApiArg
    >({
      query: (queryArg) => ({ url: `/firmware/${queryArg.id}` }),
    }),
    updateFirmware: build.mutation<
      UpdateFirmwareApiResponse,
      UpdateFirmwareApiArg
    >({
      query: (queryArg) => ({
        url: `/firmware/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.updateFirmwareDto,
      }),
    }),
    removeFirmware: build.mutation<
      RemoveFirmwareApiResponse,
      RemoveFirmwareApiArg
    >({
      query: (queryArg) => ({
        url: `/firmware/${queryArg.id}`,
        method: 'DELETE',
      }),
    }),
    getFirmware: build.query<GetFirmwareApiResponse, GetFirmwareApiArg>({
      query: (queryArg) => ({
        url: `/analyzer_types/${queryArg.id}/current_firmware`,
      }),
    }),
    createTypeLink: build.mutation<
      CreateTypeLinkApiResponse,
      CreateTypeLinkApiArg
    >({
      query: (queryArg) => ({
        url: `/hardware-type-links`,
        method: 'POST',
        body: queryArg.createHardwareTypeLinkDto,
      }),
    }),
    findAllTypeLinks: build.query<
      FindAllTypeLinksApiResponse,
      FindAllTypeLinksApiArg
    >({
      query: () => ({ url: `/hardware-type-links` }),
    }),
    findAllTypeLinksForUser: build.query<
      FindAllTypeLinksForUserApiResponse,
      FindAllTypeLinksForUserApiArg
    >({
      query: () => ({ url: `/hardware-type-links/for-user` }),
    }),
    findOneTypeLink: build.query<
      FindOneTypeLinkApiResponse,
      FindOneTypeLinkApiArg
    >({
      query: (queryArg) => ({ url: `/hardware-type-links/${queryArg.id}` }),
    }),
    updateTypeLink: build.mutation<
      UpdateTypeLinkApiResponse,
      UpdateTypeLinkApiArg
    >({
      query: (queryArg) => ({
        url: `/hardware-type-links/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.updateHardwareTypeLinkDto,
      }),
    }),
    removeTypeLink: build.mutation<
      RemoveTypeLinkApiResponse,
      RemoveTypeLinkApiArg
    >({
      query: (queryArg) => ({
        url: `/hardware-type-links/${queryArg.id}`,
        method: 'DELETE',
      }),
    }),
    createInventoryPart: build.mutation<
      CreateInventoryPartApiResponse,
      CreateInventoryPartApiArg
    >({
      query: (queryArg) => ({
        url: `/inventory-parts`,
        method: 'POST',
        body: queryArg.createInventoryPartDto,
      }),
    }),
    findAllInventoryParts: build.query<
      FindAllInventoryPartsApiResponse,
      FindAllInventoryPartsApiArg
    >({
      query: () => ({ url: `/inventory-parts` }),
    }),
    findOneInventoryPart: build.query<
      FindOneInventoryPartApiResponse,
      FindOneInventoryPartApiArg
    >({
      query: (queryArg) => ({ url: `/inventory-parts/${queryArg.id}` }),
    }),
    updateInventoryPart: build.mutation<
      UpdateInventoryPartApiResponse,
      UpdateInventoryPartApiArg
    >({
      query: (queryArg) => ({
        url: `/inventory-parts/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.updateInventoryPartDto,
      }),
    }),
    removeInventoryPart: build.mutation<
      RemoveInventoryPartApiResponse,
      RemoveInventoryPartApiArg
    >({
      query: (queryArg) => ({
        url: `/inventory-parts/${queryArg.id}`,
        method: 'DELETE',
      }),
    }),
    createInventoryPartUsed: build.mutation<
      CreateInventoryPartUsedApiResponse,
      CreateInventoryPartUsedApiArg
    >({
      query: (queryArg) => ({
        url: `/inventory-parts-used`,
        method: 'POST',
        body: queryArg.createInventoryPartsUsedDto,
      }),
    }),
    findAllInventoryPartsUsed: build.query<
      FindAllInventoryPartsUsedApiResponse,
      FindAllInventoryPartsUsedApiArg
    >({
      query: () => ({ url: `/inventory-parts-used` }),
    }),
    findOneInventoryPartUsed: build.query<
      FindOneInventoryPartUsedApiResponse,
      FindOneInventoryPartUsedApiArg
    >({
      query: (queryArg) => ({ url: `/inventory-parts-used/${queryArg.id}` }),
    }),
    updateInventoryPartUsed: build.mutation<
      UpdateInventoryPartUsedApiResponse,
      UpdateInventoryPartUsedApiArg
    >({
      query: (queryArg) => ({
        url: `/inventory-parts-used/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.updateInventoryPartsUsedDto,
      }),
    }),
    removeInventoryPartUsed: build.mutation<
      RemoveInventoryPartUsedApiResponse,
      RemoveInventoryPartUsedApiArg
    >({
      query: (queryArg) => ({
        url: `/inventory-parts-used/${queryArg.id}`,
        method: 'DELETE',
      }),
    }),
    createRma: build.mutation<CreateRmaApiResponse, CreateRmaApiArg>({
      query: (queryArg) => ({
        url: `/rmas`,
        method: 'POST',
        body: queryArg.createRmaDto,
      }),
    }),
    findAllRmas: build.query<FindAllRmasApiResponse, FindAllRmasApiArg>({
      query: () => ({ url: `/rmas` }),
    }),
    sendRmaEmail: build.mutation<SendRmaEmailApiResponse, SendRmaEmailApiArg>({
      query: (queryArg) => ({
        url: `/rmas/email`,
        method: 'POST',
        body: queryArg.sendEmailDto,
      }),
    }),
    sendDepartmentEmail: build.mutation<
      SendDepartmentEmailApiResponse,
      SendDepartmentEmailApiArg
    >({
      query: (queryArg) => ({
        url: `/departments/email`,
        method: 'POST',
        body: queryArg.sendDepartmentEmailDto,
      }),
    }),
    findAllRmasForUser: build.query<
      FindAllRmasForUserApiResponse,
      FindAllRmasForUserApiArg
    >({
      query: () => ({ url: `/rmas/for-user` }),
    }),
    findOneRma: build.query<FindOneRmaApiResponse, FindOneRmaApiArg>({
      query: (queryArg) => ({ url: `/rmas/${queryArg.id}` }),
    }),
    updateRma: build.mutation<UpdateRmaApiResponse, UpdateRmaApiArg>({
      query: (queryArg) => ({
        url: `/rmas/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.updateRmaDto,
      }),
    }),
    removeRma: build.mutation<RemoveRmaApiResponse, RemoveRmaApiArg>({
      query: (queryArg) => ({ url: `/rmas/${queryArg.id}`, method: 'DELETE' }),
    }),
    requestReset: build.mutation<RequestResetApiResponse, RequestResetApiArg>({
      query: (queryArg) => ({
        url: `/password-resets/create`,
        method: 'POST',
        body: queryArg.createPasswordResetDto,
      }),
    }),
    processReset: build.mutation<ProcessResetApiResponse, ProcessResetApiArg>({
      query: (queryArg) => ({
        url: `/password-resets/process`,
        method: 'PUT',
        body: queryArg.processResetDto,
      }),
    }),
    createRepairComment: build.mutation<
      CreateRepairCommentApiResponse,
      CreateRepairCommentApiArg
    >({
      query: (queryArg) => ({
        url: `/repair-comments`,
        method: 'POST',
        body: queryArg.createRepairCommentDto,
      }),
    }),
    findAllRepairComments: build.query<
      FindAllRepairCommentsApiResponse,
      FindAllRepairCommentsApiArg
    >({
      query: () => ({ url: `/repair-comments` }),
    }),
    findOneRepairComment: build.query<
      FindOneRepairCommentApiResponse,
      FindOneRepairCommentApiArg
    >({
      query: (queryArg) => ({ url: `/repair-comments/${queryArg.id}` }),
    }),
    updateRepairComment: build.mutation<
      UpdateRepairCommentApiResponse,
      UpdateRepairCommentApiArg
    >({
      query: (queryArg) => ({
        url: `/repair-comments/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.updateRepairCommentDto,
      }),
    }),
    removeRepairComment: build.mutation<
      RemoveRepairCommentApiResponse,
      RemoveRepairCommentApiArg
    >({
      query: (queryArg) => ({
        url: `/repair-comments/${queryArg.id}`,
        method: 'DELETE',
      }),
    }),
    findRepairCommentsByRma: build.query<
      FindRepairCommentsByRmaApiResponse,
      FindRepairCommentsByRmaApiArg
    >({
      query: (queryArg) => ({ url: `/repair-comments/rma/${queryArg.id}` }),
    }),
    createSignup: build.mutation<CreateSignupApiResponse, CreateSignupApiArg>({
      query: (queryArg) => ({
        url: `/signups`,
        method: 'POST',
        body: queryArg.createSignupDto,
      }),
    }),
    findAllSignups: build.query<
      FindAllSignupsApiResponse,
      FindAllSignupsApiArg
    >({
      query: () => ({ url: `/signups` }),
    }),
    findOneSignup: build.query<FindOneSignupApiResponse, FindOneSignupApiArg>({
      query: (queryArg) => ({ url: `/signups/${queryArg.id}` }),
    }),
    updateSignup: build.mutation<UpdateSignupApiResponse, UpdateSignupApiArg>({
      query: (queryArg) => ({
        url: `/signups/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.updateSignupDto,
      }),
    }),
    removeSignup: build.mutation<RemoveSignupApiResponse, RemoveSignupApiArg>({
      query: (queryArg) => ({
        url: `/signups/${queryArg.id}`,
        method: 'DELETE',
      }),
    }),
    createSoftwareLink: build.mutation<
      CreateSoftwareLinkApiResponse,
      CreateSoftwareLinkApiArg
    >({
      query: (queryArg) => ({
        url: `/software-links`,
        method: 'POST',
        body: queryArg.createSoftwareLinkDto,
      }),
    }),
    findAllSoftwareLinks: build.query<
      FindAllSoftwareLinksApiResponse,
      FindAllSoftwareLinksApiArg
    >({
      query: () => ({ url: `/software-links` }),
    }),
    findAllSoftwareLinksForUser: build.query<
      FindAllSoftwareLinksForUserApiResponse,
      FindAllSoftwareLinksForUserApiArg
    >({
      query: () => ({ url: `/software-links/for-user` }),
    }),
    findOneSoftwareLink: build.query<
      FindOneSoftwareLinkApiResponse,
      FindOneSoftwareLinkApiArg
    >({
      query: (queryArg) => ({ url: `/software-links/${queryArg.id}` }),
    }),
    updateSoftwareLink: build.mutation<
      UpdateSoftwareLinkApiResponse,
      UpdateSoftwareLinkApiArg
    >({
      query: (queryArg) => ({
        url: `/software-links/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.updateSoftwareLinkDto,
      }),
    }),
    removeSoftwareLink: build.mutation<
      RemoveSoftwareLinkApiResponse,
      RemoveSoftwareLinkApiArg
    >({
      query: (queryArg) => ({
        url: `/software-links/${queryArg.id}`,
        method: 'DELETE',
      }),
    }),
    createSupportPurchase: build.mutation<
      CreateSupportPurchaseApiResponse,
      CreateSupportPurchaseApiArg
    >({
      query: (queryArg) => ({
        url: `/support-purchases`,
        method: 'POST',
        body: queryArg.createSupportPurchaseDto,
      }),
    }),
    findAllSupportPurchases: build.query<
      FindAllSupportPurchasesApiResponse,
      FindAllSupportPurchasesApiArg
    >({
      query: () => ({ url: `/support-purchases` }),
    }),
    findOneSupportPurchase: build.query<
      FindOneSupportPurchaseApiResponse,
      FindOneSupportPurchaseApiArg
    >({
      query: (queryArg) => ({ url: `/support-purchases/${queryArg.id}` }),
    }),
    updateSupportPurchase: build.mutation<
      UpdateSupportPurchaseApiResponse,
      UpdateSupportPurchaseApiArg
    >({
      query: (queryArg) => ({
        url: `/support-purchases/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.updateSupportPurchaseDto,
      }),
    }),
    removeSupportPurchase: build.mutation<
      RemoveSupportPurchaseApiResponse,
      RemoveSupportPurchaseApiArg
    >({
      query: (queryArg) => ({
        url: `/support-purchases/${queryArg.id}`,
        method: 'DELETE',
      }),
    }),
    createUtility: build.mutation<
      CreateUtilityApiResponse,
      CreateUtilityApiArg
    >({
      query: (queryArg) => ({
        url: `/utilities`,
        method: 'POST',
        body: queryArg.createUtilityDto,
      }),
    }),
    findAllUtilities: build.query<
      FindAllUtilitiesApiResponse,
      FindAllUtilitiesApiArg
    >({
      query: () => ({ url: `/utilities` }),
    }),
    findOneUtility: build.query<
      FindOneUtilityApiResponse,
      FindOneUtilityApiArg
    >({
      query: (queryArg) => ({ url: `/utilities/${queryArg.id}` }),
    }),
    updateUtility: build.mutation<
      UpdateUtilityApiResponse,
      UpdateUtilityApiArg
    >({
      query: (queryArg) => ({
        url: `/utilities/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.updateUtilityDto,
      }),
    }),
    removeUtility: build.mutation<
      RemoveUtilityApiResponse,
      RemoveUtilityApiArg
    >({
      query: (queryArg) => ({
        url: `/utilities/${queryArg.id}`,
        method: 'DELETE',
      }),
    }),
    createSystemModuleLicense: build.mutation<
      CreateSystemModuleLicenseApiResponse,
      CreateSystemModuleLicenseApiArg
    >({
      query: (queryArg) => ({
        url: `/system-module-licenses`,
        method: 'POST',
        body: queryArg.createSystemModuleLicenseDto,
      }),
    }),
    findAllSystemModuleLicenses: build.query<
      FindAllSystemModuleLicensesApiResponse,
      FindAllSystemModuleLicensesApiArg
    >({
      query: () => ({ url: `/system-module-licenses` }),
    }),
    findAllSystemModuleLicensesByCompanyId: build.query<
      FindAllSystemModuleLicensesByCompanyIdApiResponse,
      FindAllSystemModuleLicensesByCompanyIdApiArg
    >({
      query: (queryArg) => ({
        url: `/system-module-licenses/company/${queryArg.id}`,
      }),
    }),
    findSystemModuleLicensesForUser: build.query<
      FindSystemModuleLicensesForUserApiResponse,
      FindSystemModuleLicensesForUserApiArg
    >({
      query: () => ({ url: `/system-module-licenses/for-user` }),
    }),
    findOneSystemModuleLicense: build.query<
      FindOneSystemModuleLicenseApiResponse,
      FindOneSystemModuleLicenseApiArg
    >({
      query: (queryArg) => ({ url: `/system-module-licenses/${queryArg.id}` }),
    }),
    updateSystemModuleLicense: build.mutation<
      UpdateSystemModuleLicenseApiResponse,
      UpdateSystemModuleLicenseApiArg
    >({
      query: (queryArg) => ({
        url: `/system-module-licenses/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.updateSystemModuleLicenseDto,
      }),
    }),
    removeSystemModuleLicense: build.mutation<
      RemoveSystemModuleLicenseApiResponse,
      RemoveSystemModuleLicenseApiArg
    >({
      query: (queryArg) => ({
        url: `/system-module-licenses/${queryArg.id}`,
        method: 'DELETE',
      }),
    }),
    parseSystemModuleLicenses: build.mutation<
      ParseSystemModuleLicensesApiResponse,
      ParseSystemModuleLicensesApiArg
    >({
      query: (queryArg) => ({
        url: `/system-module-licenses/parse-and-save`,
        method: 'PUT',
        body: queryArg.parseSystemModuleLicenseDto,
      }),
    }),
    findAlldepartments: build.query<
      FindAlldepartmentsApiResponse,
      FindAlldepartmentsApiArg
    >({
      query: () => ({ url: `/departments` }),
    }),
    createDepartment: build.mutation<
      CreateDepartmentApiResponse,
      CreateDepartmentApiArg
    >({
      query: (queryArg) => ({
        url: `/departments`,
        method: 'POST',
        body: queryArg.createDepartmentDto,
      }),
    }),
    findOneDepartment: build.query<
      FindOneDepartmentApiResponse,
      FindOneDepartmentApiArg
    >({
      query: (queryArg) => ({ url: `/departments/${queryArg.id}` }),
    }),
    updateDepartment: build.mutation<
      UpdateDepartmentApiResponse,
      UpdateDepartmentApiArg
    >({
      query: (queryArg) => ({
        url: `/departments/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.updateDepartmentDto,
      }),
    }),
    deleteDepartment: build.mutation<
      DeleteDepartmentApiResponse,
      DeleteDepartmentApiArg
    >({
      query: (queryArg) => ({
        url: `/departments/${queryArg.id}`,
        method: 'DELETE',
      }),
    }),
  }),
});
export type GetHelloApiResponse = /** status 200  */ string;
export type GetHelloApiArg = {};
export type LoginApiResponse = {
  user: UserNoPassword;
  token: string;
};
export type LoginApiArg = {
  email: string;
  password: string;
};
export type LogoutApiResponse = unknown;
export type LogoutApiArg = {
  id: number;
};
export type RefreshApiResponse = unknown;
export type RefreshApiArg = {
  refreshDto: RefreshDto;
};
export type CreateUserApiResponse = /** status 201  */ object;
export type CreateUserApiArg = {
  createUserDto: CreateUserDto;
};
export type FindAllUsersApiResponse = /** status 200  */ UserNoPassword[];
export type FindAllUsersApiArg = {};
export type CreateUserNoHashApiResponse = /** status 201  */ object;
export type CreateUserNoHashApiArg = {
  createUserDto: CreateUserDto;
};
export type FindUserForUserApiResponse = UserProfile;
export type FindUserForUserApiArg = {};
export type UpdateForUserApiResponse = /** status 200  */ object;
export type UpdateForUserApiArg = {
  updateUserDto: UpdateUserDto;
};
export type FindAllUsersForUserApiResponse = unknown;
export type FindAllUsersForUserApiArg = {};
export type FindOneUserApiResponse = /** status 200  */ object;
export type FindOneUserApiArg = {
  id: number;
};
export type UpdateUserApiResponse = /** status 200  */ object;
export type UpdateUserApiArg = {
  id: number;
  updateUserDto: UpdateUserDto;
};
export type RemoveUserApiResponse = unknown;
export type RemoveUserApiArg = {
  id: number;
};
export type UpdateEmailPasswordForUserApiResponse = /** status 200  */ object;
export type UpdateEmailPasswordForUserApiArg = {
  changePasswordDto: ChangePasswordDto;
};
export type FindAllCompaniesApiResponse = /** status 200  */ Omit<
  Company,
  'licenses' | 'hardware' | 'rmas' | 'users' | 'demos' | 'orders'
>[];
export type FindAllCompaniesApiArg = {};
export type CreateCompanyApiResponse = /** status 201  */ object;
export type CreateCompanyApiArg = {
  createCompanyDto: CreateCompanyDto;
};
export type FindOneCompanyApiResponse = /** status 200  */ Omit<
  Company,
  'licenses' | 'hardware' | 'rmas' | 'users' | 'demos' | 'orders'
>;
export type FindOneCompanyApiArg = {
  id: number;
};
export type UpdateCompanyApiResponse = /** status 200  */ object;
export type UpdateCompanyApiArg = {
  id: number;
  updateCompanyDto: UpdateCompanyDto;
};
export type DeleteCompanyApiResponse = unknown;
export type DeleteCompanyApiArg = {
  id: number;
};
export type CreateDemoApiResponse = /** status 201  */ object;
export type CreateDemoApiArg = {
  createDemoDto: CreateDemoDto;
};
export type FindAllDemosApiResponse = /** status 200  */ Demo[];
export type FindAllDemosApiArg = {};
export type FindAllDemosForUserApiResponse = /** status 200  */ Demo[];
export type FindAllDemosForUserApiArg = {};
export type FindOneDemosApiResponse = /** status 200  */ Demo;
export type FindOneDemosApiArg = {
  id: number;
};
export type UpdateDemoApiResponse = /** status 200  */ object;
export type UpdateDemoApiArg = {
  id: number;
  updateDemoDto: UpdateDemoDto;
};
export type RemoveDemoApiResponse = unknown;
export type RemoveDemoApiArg = {
  id: number;
};
export type CreateSoftwareApiResponse = /** status 201  */ Software;
export type CreateSoftwareApiArg = {
  createSoftwareDto: CreateSoftwareDto;
};
export type FindAllSoftwareApiResponse = /** status 200  */ Software[];
export type FindAllSoftwareApiArg = {};
export type FindMostRecentVersionNameApiResponse = /** status 200  */ string;
export type FindMostRecentVersionNameApiArg = {
  id: number;
};
export type FindOneSoftwareApiResponse = /** status 200  */ Software;
export type FindOneSoftwareApiArg = {
  id: number;
};
export type UpdateSoftwareApiResponse = /** status 200  */ Software;
export type UpdateSoftwareApiArg = {
  id: number;
  updateSoftwareDto: UpdateSoftwareDto;
};
export type RemoveSoftwareApiResponse = unknown;
export type RemoveSoftwareApiArg = {
  id: number;
};
export type CreateSoftwareVersionApiResponse =
  /** status 201  */ SoftwareVersion;
export type CreateSoftwareVersionApiArg = {
  createSoftwareVersionDto: CreateSoftwareVersionDto;
};
export type FindAllSoftwareVersionsApiResponse =
  /** status 200  */ SoftwareVersion[];
export type FindAllSoftwareVersionsApiArg = {};
export type FindAllSoftwareVersionsForUserApiResponse =
  /** status 200  */ SoftwareVersion[];
export type FindAllSoftwareVersionsForUserApiArg = {};
export type FindOneSoftwareVersionApiResponse =
  /** status 200  */ SoftwareVersion;
export type FindOneSoftwareVersionApiArg = {
  id: number;
};
export type UpdateSoftwareVersionApiResponse =
  /** status 200  */ SoftwareVersion;
export type UpdateSoftwareVersionApiArg = {
  id: number;
  updateSoftwareVersionDto: UpdateSoftwareVersionDto;
};
export type RemoveSoftwareVersionApiResponse = unknown;
export type RemoveSoftwareVersionApiArg = {
  id: number;
};
export type FindLatestSoftwareVersionApiResponse =
  /** status 200  */ SoftwareVersion;
export type FindLatestSoftwareVersionApiArg = {
  id: number;
};
export type SendReleaseEmailApiResponse = unknown;
export type SendReleaseEmailApiArg = {
  sendVersionReleaseEmailDto: SendVersionReleaseEmailDto;
};
export type CreateOperatingSystemApiResponse = /** status 201  */ object;
export type CreateOperatingSystemApiArg = {
  createOperatingSystemDto: CreateOperatingSystemDto;
};
export type FindAllOperatingSystemsApiResponse =
  /** status 200  */ OperatingSystem[];
export type FindAllOperatingSystemsApiArg = {};
export type FindOneOperatingSystemApiResponse =
  /** status 200  */ OperatingSystem;
export type FindOneOperatingSystemApiArg = {
  id: number;
};
export type UpdateOperatingSystemApiResponse = /** status 200  */ object;
export type UpdateOperatingSystemApiArg = {
  id: number;
  updateOperatingSystemDto: UpdateOperatingSystemDto;
};
export type RemoveOperatingSystemApiResponse = unknown;
export type RemoveOperatingSystemApiArg = {
  id: number;
};
export type CreateLicenseApiResponse = /** status 201  */ object;
export type CreateLicenseApiArg = {
  createLicenseDto: CreateLicenseDto;
};
export type FindAllLicensesApiResponse = /** status 200  */ License[];
export type FindAllLicensesApiArg = {};
export type FindAllLicensesByCompanyIdApiResponse =
  /** status 200  */ License[];
export type FindAllLicensesByCompanyIdApiArg = {
  id: number;
};
export type FindLicensesForUserApiResponse = /** status 200  */ License[];
export type FindLicensesForUserApiArg = {};
export type FindOneLicenseApiResponse = /** status 200  */ License;
export type FindOneLicenseApiArg = {
  id: number;
};
export type UpdateLicenseApiResponse = /** status 200  */ object;
export type UpdateLicenseApiArg = {
  id: number;
  updateLicenseDto: UpdateLicenseDto;
};
export type RemoveLicenseApiResponse = unknown;
export type RemoveLicenseApiArg = {
  id: number;
};
export interface ParsedLicense {
  analyzer: { serial: string; baseLicense: string };
  modules: { type: string; moduleLicense: string }[];
}
export type ParseLicensesApiResponse = /** status 200  */ ParsedLicense[];
export type ParseLicensesApiArg = {
  parseLicenseDto: ParseLicenseDto;
};
export type CreateHardwareApiResponse = /** status 201  */ object;
export type CreateHardwareApiArg = {
  createHardwareDto: CreateHardwareDto;
};
export type FindAllHardwareApiResponse = /** status 200  */ Omit<
  Hardware,
  'licenses'
>[];
export type FindAllHardwareApiArg = {};
export type FindAllHardwareForUserApiResponse = /** status 200  */ Omit<
  Hardware,
  'order' | 'companies'
>[];
export type FindAllDepartmentsForUserApiResponse =
  /** status 200  */ Department[];
export type FindAllHardwareForUserApiArg = {};
export type FindAllDepartmentsForUserApiArg = {};
export type FindOneHardwareApiResponse = /** status 200  */ Hardware;
export type FindOneHardwareApiArg = {
  id: number;
};
export type UpdateHardwareApiResponse = /** status 200  */ object;
export type UpdateHardwareApiArg = {
  id: number;
  updateHardwareDto: UpdateHardwareDto;
};
export type RemoveHardwareApiResponse = unknown;
export type RemoveHardwareApiArg = {
  id: number;
};
export type CreateTypeApiResponse = /** status 201  */ HardwareType;
export type CreateTypeApiArg = {
  createHardwareTypeDto: CreateHardwareTypeDto;
};
export type GetAllTypesApiResponse = /** status 200  */ HardwareType[];
export type GetAllTypesApiArg = {};
export type GetTypeByIdApiResponse = /** status 200  */ HardwareType;
export type GetTypeByIdApiArg = {
  id: number;
};
export type DeleteTypeApiResponse = unknown;
export type DeleteTypeApiArg = {
  id: number;
};
export type UpdateTypeApiResponse = /** status 200  */ HardwareType;
export type UpdateTypeApiArg = {
  id: number;
  updateHardwareTypeDto: UpdateHardwareTypeDto;
};
export type CreateHardwareCategoryApiResponse = /** status 201  */ object;
export type CreateHardwareCategoryApiArg = {
  createHardwareCategoryDto: CreateHardwareCategoryDto;
};
export type FindAllHardwareCategoriesApiResponse =
  /** status 200  */ HardwareCategory[];
export type FindAllHardwareCategoriesApiArg = {};
export type FindOneHardwareCategoryApiResponse =
  /** status 200  */ HardwareCategory;
export type FindOneHardwareCategoryApiArg = {
  id: number;
};
export type UpdateHardwareCategoryApiResponse = /** status 200  */ object;
export type UpdateHardwareCategoryApiArg = {
  id: number;
  updateHardwareCategoryDto: UpdateHardwareCategoryDto;
};
export type RemoveHardwareCategoryApiResponse = unknown;
export type RemoveHardwareCategoryApiArg = {
  id: number;
};
export type CreateModuleApiResponse = /** status 201  */ object;
export type CreateModuleApiArg = {
  createModuleDto: CreateModuleDto;
};
export type FindAllModulesApiResponse = /** status 200  */ Module[];
export type FindAllModulesApiArg = {};
export type FindOneModuleApiResponse = /** status 200  */ Module;
export type FindOneModuleApiArg = {
  id: number;
};
export type UpdateModuleApiResponse = /** status 200  */ object;
export type UpdateModuleApiArg = {
  id: number;
  updateModuleDto: UpdateModuleDto;
};
export type RemoveModuleApiResponse = unknown;
export type RemoveModuleApiArg = {
  id: number;
};
export type CreateOrderApiResponse = /** status 201  */ Order;
export type CreateOrderApiArg = {
  createOrderDto: CreateOrderDto;
};
export type FindAllOrdersApiResponse = /** status 200  */ Order[];
export type FindAllOrdersApiArg = {};
export type FindOrdersForUserApiResponse = /** status 200  */ Order[];
export type FindOrdersForUserApiArg = {};
export type FindOneOrderApiResponse = /** status 200  */ Order;
export type FindOneOrderApiArg = {
  id: number;
};
export type UpdateOrderApiResponse = /** status 200  */ Order;
export type UpdateOrderApiArg = {
  id: number;
  updateOrderDto: UpdateOrderDto;
};
export type RemoveOrderApiResponse = unknown;
export type RemoveOrderApiArg = {
  id: number;
};
export type DownloadQuoteApiResponse = unknown;
export type DownloadQuoteApiArg = {
  id: number;
};
export type DownloadPoApiResponse = unknown;
export type DownloadPoApiArg = {
  id: number;
};
export type UploadPurchaseOrderApiResponse /** status 201  */ =
  | { error: string }
  | { downloadUrl: string };
export type UploadPurchaseOrderApiArg = {};
export type UploadQuoteApiResponse = /** status 201  */ object;
export type UploadQuoteApiArg = {};
export type CreateInventoryHardwareApiResponse =
  /** status 201  */ InventoryHardware;
export type CreateInventoryHardwareApiArg = {
  createInventoryHardwareDto: CreateInventoryHardwareDto;
};
export type FindAllInventoryHardwareApiResponse =
  /** status 200  */ InventoryHardware[];
export type FindAllInventoryHardwareApiArg = {};
export type FindOneInventoryHardwareApiResponse =
  /** status 200  */ InventoryHardware;
export type FindOneInventoryHardwareApiArg = {
  id: number;
};
export type UpdateInventoryHardwareApiResponse = /** status 200  */ object;
export type UpdateInventoryHardwareApiArg = {
  id: number;
  updateInventoryHardwareDto: UpdateInventoryHardwareDto;
};
export type RemoveInventoryHardwareApiResponse = unknown;
export type RemoveInventoryHardwareApiArg = {
  id: number;
};
export type CreateFirmwareApiResponse = /** status 201  */ object;
export type CreateFirmwareApiArg = {
  createFirmwareDto: CreateFirmwareDto;
};
export type FindAllFirmwareApiResponse = /** status 200  */ Firmware[];
export type FindAllFirmwareApiArg = {};
export type FindAllFirmwareForUserApiResponse = /** status 200  */ Firmware[];
export type FindAllFirmwareForUserApiArg = {};
export type FindOneFirmwareApiResponse = /** status 200  */ Firmware;
export type FindOneFirmwareApiArg = {
  id: number;
};
export type UpdateFirmwareApiResponse = /** status 200  */ object;
export type UpdateFirmwareApiArg = {
  id: number;
  updateFirmwareDto: UpdateFirmwareDto;
};
export type RemoveFirmwareApiResponse = unknown;
export type RemoveFirmwareApiArg = {
  id: number;
};
export type GetFirmwareApiResponse = /** status 200  */ string;
export type GetFirmwareApiArg = {
  id: number;
};
export type CreateTypeLinkApiResponse = /** status 201  */ HardwareTypeLink;
export type CreateTypeLinkApiArg = {
  createHardwareTypeLinkDto: CreateHardwareTypeLinkDto;
};
export type FindAllTypeLinksApiResponse = /** status 200  */ HardwareTypeLink[];
export type FindAllTypeLinksApiArg = {};
export type FindAllTypeLinksForUserApiResponse =
  /** status 200  */ HardwareTypeLink[];
export type FindAllTypeLinksForUserApiArg = {};
export type FindOneTypeLinkApiResponse = /** status 200  */ HardwareTypeLink;
export type FindOneTypeLinkApiArg = {
  id: number;
};
export type UpdateTypeLinkApiResponse = /** status 200  */ HardwareTypeLink;
export type UpdateTypeLinkApiArg = {
  id: number;
  updateHardwareTypeLinkDto: UpdateHardwareTypeLinkDto;
};
export type RemoveTypeLinkApiResponse = unknown;
export type RemoveTypeLinkApiArg = {
  id: number;
};
export type CreateInventoryPartApiResponse = /** status 201  */ InventoryPart;
export type CreateInventoryPartApiArg = {
  createInventoryPartDto: CreateInventoryPartDto;
};
export type FindAllInventoryPartsApiResponse =
  /** status 200  */ InventoryPart[];
export type FindAllInventoryPartsApiArg = {};
export type FindOneInventoryPartApiResponse = /** status 200  */ InventoryPart;
export type FindOneInventoryPartApiArg = {
  id: number;
};
export type UpdateInventoryPartApiResponse = /** status 200  */ InventoryPart;
export type UpdateInventoryPartApiArg = {
  id: number;
  updateInventoryPartDto: UpdateInventoryPartDto;
};
export type RemoveInventoryPartApiResponse = unknown;
export type RemoveInventoryPartApiArg = {
  id: number;
};
export type CreateInventoryPartUsedApiResponse =
  /** status 201  */ InventoryPartUsed;
export type CreateInventoryPartUsedApiArg = {
  createInventoryPartsUsedDto: CreateInventoryPartsUsedDto;
};
export type FindAllInventoryPartsUsedApiResponse =
  /** status 200  */ InventoryPartUsed[];
export type FindAllInventoryPartsUsedApiArg = {};
export type FindOneInventoryPartUsedApiResponse =
  /** status 200  */ InventoryPartUsed;
export type FindOneInventoryPartUsedApiArg = {
  id: number;
};
export type UpdateInventoryPartUsedApiResponse =
  /** status 200  */ InventoryPartUsed;
export type UpdateInventoryPartUsedApiArg = {
  id: number;
  updateInventoryPartsUsedDto: UpdateInventoryPartsUsedDto;
};
export type RemoveInventoryPartUsedApiResponse = unknown;
export type RemoveInventoryPartUsedApiArg = {
  id: number;
};
export type CreateRmaApiResponse = /** status 201  */ object;
export type CreateRmaApiArg = {
  createRmaDto: CreateRmaDto;
};
export type FindAllRmasApiResponse = /** status 200  */ Rma[];
export type FindAllRmasApiArg = {};
export type SendRmaEmailApiResponse = /** status 201  */ object;
export type SendRmaEmailApiArg = {
  sendEmailDto: SendEmailDto;
};
export type SendDepartmentEmailApiResponse = /** status 201  */ object;
export type SendDepartmentEmailApiArg = {
  sendDepartmentEmailDto: SendDepartmentEmailDto;
};
export type FindAllRmasForUserApiResponse = /** status 200  */ Rma[];
export type FindAllRmasForUserApiArg = {};
export type FindOneRmaApiResponse = /** status 200  */ Rma;
export type FindOneRmaApiArg = {
  id: number;
};
export type UpdateRmaApiResponse = /** status 200  */ object;
export type UpdateRmaApiArg = {
  id: number;
  updateRmaDto: UpdateRmaDto;
};
export type RemoveRmaApiResponse = unknown;
export type RemoveRmaApiArg = {
  id: number;
};
export type RequestResetApiResponse = unknown;
export type RequestResetApiArg = {
  createPasswordResetDto: CreatePasswordResetDto;
};
export type ProcessResetApiResponse = {
  error?: string;
  message?: string;
};
export type ProcessResetApiArg = {
  processResetDto: ProcessResetDto;
};
export type CreateRepairCommentApiResponse = /** status 201  */ object;
export type CreateRepairCommentApiArg = {
  createRepairCommentDto: CreateRepairCommentDto;
};
export type FindAllRepairCommentsApiResponse =
  /** status 200  */ RepairComment[];
export type FindAllRepairCommentsApiArg = {};
export type FindOneRepairCommentApiResponse = /** status 200  */ RepairComment;
export type FindOneRepairCommentApiArg = {
  id: number;
};
export type UpdateRepairCommentApiResponse = /** status 200  */ object;
export type UpdateRepairCommentApiArg = {
  id: number;
  updateRepairCommentDto: UpdateRepairCommentDto;
};
export type RemoveRepairCommentApiResponse = unknown;
export type RemoveRepairCommentApiArg = {
  id: number;
};
export type FindRepairCommentsByRmaApiResponse =
  /** status 200  */ RepairComment[];
export type FindRepairCommentsByRmaApiArg = {
  id: number;
};
export type CreateSignupApiResponse = unknown;
export type CreateSignupApiArg = {
  createSignupDto: CreateSignupDto;
};
export type FindAllSignupsApiResponse = /** status 200  */ Signup[];
export type FindAllSignupsApiArg = {};
export type FindOneSignupApiResponse = /** status 200  */ Signup;
export type FindOneSignupApiArg = {
  id: number;
};
export type UpdateSignupApiResponse = /** status 200  */ object;
export type UpdateSignupApiArg = {
  id: number;
  updateSignupDto: UpdateSignupDto;
};
export type RemoveSignupApiResponse = unknown;
export type RemoveSignupApiArg = {
  id: number;
};
export type CreateSoftwareLinkApiResponse = /** status 201  */ SoftwareLink;
export type CreateSoftwareLinkApiArg = {
  createSoftwareLinkDto: CreateSoftwareLinkDto;
};
export type FindAllSoftwareLinksApiResponse = /** status 200  */ SoftwareLink[];
export type FindAllSoftwareLinksApiArg = {};
export type FindAllSoftwareLinksForUserApiResponse =
  /** status 200  */ SoftwareLink[];
export type FindAllSoftwareLinksForUserApiArg = {};
export type FindOneSoftwareLinkApiResponse = /** status 200  */ SoftwareLink;
export type FindOneSoftwareLinkApiArg = {
  id: number;
};
export type UpdateSoftwareLinkApiResponse = /** status 200  */ SoftwareLink;
export type UpdateSoftwareLinkApiArg = {
  id: number;
  updateSoftwareLinkDto: UpdateSoftwareLinkDto;
};
export type RemoveSoftwareLinkApiResponse = unknown;
export type RemoveSoftwareLinkApiArg = {
  id: number;
};
export type CreateSupportPurchaseApiResponse =
  /** status 201  */ SupportPurchase;
export type CreateSupportPurchaseApiArg = {
  createSupportPurchaseDto: CreateSupportPurchaseDto;
};
export type FindAllSupportPurchasesApiResponse =
  /** status 200  */ SupportPurchase[];
export type FindAllSupportPurchasesApiArg = {};
export type FindOneSupportPurchaseApiResponse =
  /** status 200  */ SupportPurchase;
export type FindOneSupportPurchaseApiArg = {
  id: number;
};
export type UpdateSupportPurchaseApiResponse =
  /** status 200  */ SupportPurchase;
export type UpdateSupportPurchaseApiArg = {
  id: number;
  updateSupportPurchaseDto: UpdateSupportPurchaseDto;
};
export type RemoveSupportPurchaseApiResponse = unknown;
export type RemoveSupportPurchaseApiArg = {
  id: number;
};
export type CreateUtilityApiResponse = /** status 201  */ Utility;
export type CreateUtilityApiArg = {
  createUtilityDto: CreateUtilityDto;
};
export type FindAllUtilitiesApiResponse = /** status 200  */ Utility[];
export type FindAllUtilitiesApiArg = {};
export type FindOneUtilityApiResponse = /** status 200  */ Utility;
export type FindOneUtilityApiArg = {
  id: number;
};
export type UpdateUtilityApiResponse = /** status 200  */ Utility;
export type UpdateUtilityApiArg = {
  id: number;
  updateUtilityDto: UpdateUtilityDto;
};
export type RemoveUtilityApiResponse = unknown;
export type RemoveUtilityApiArg = {
  id: number;
};
export type FindAlldepartmentsApiResponse = /** status 200  */ Department[];
export type FindAlldepartmentsApiArg = {};
export type CreateDepartmentApiResponse = /** status 201  */ Department;
export type CreateDepartmentApiArg = {
  createDepartmentDto: CreateDepartmentDto;
};
export type FindOneDepartmentApiResponse = /** status 200  */ Department;
export type FindOneDepartmentApiArg = {
  id: number;
};
export type UpdateDepartmentApiResponse = /** status 200  */ Department;
export type UpdateDepartmentApiArg = {
  id: number;
  updateDepartmentDto: UpdateDepartmentDto;
};
export type DeleteDepartmentApiResponse = unknown;
export type DeleteDepartmentApiArg = {
  id: number;
};
export type CreateSystemModuleLicenseApiResponse = /** status 201  */ object;
export type CreateSystemModuleLicenseApiArg = {
  createSystemModuleLicenseDto: CreateSystemModuleLicenseDto;
};
export type FindAllSystemModuleLicensesApiResponse =
  /** status 200  */ SystemModuleLicense[];
export type FindAllSystemModuleLicensesApiArg = void;
export type FindAllSystemModuleLicensesByCompanyIdApiResponse =
  /** status 200  */ SystemModuleLicense[];
export type FindAllSystemModuleLicensesByCompanyIdApiArg = {
  id: number;
};
export type FindSystemModuleLicensesForUserApiResponse =
  /** status 200  */ SystemModuleLicense[];
export type FindSystemModuleLicensesForUserApiArg = void;
export type FindOneSystemModuleLicenseApiResponse =
  /** status 200  */ SystemModuleLicense;
export type FindOneSystemModuleLicenseApiArg = {
  id: number;
};
export type UpdateSystemModuleLicenseApiResponse = /** status 200  */ object;
export type UpdateSystemModuleLicenseApiArg = {
  id: number;
  updateSystemModuleLicenseDto: UpdateSystemModuleLicenseDto;
};
export type RemoveSystemModuleLicenseApiResponse = unknown;
export type RemoveSystemModuleLicenseApiArg = {
  id: number;
};
export type ParseSystemModuleLicensesApiResponse = unknown;
export type ParseSystemModuleLicensesApiArg = {
  parseSystemModuleLicenseDto: ParseSystemModuleLicenseDto;
};
export type RefreshDto = {
  id: number;
  refreshToken: string;
};
export type CreateUserDto = {
  id?: number;
  name: string;
  phone?: string;
  address?: string;
  email: string;
  password: string;
  role: number;
  companyId: number;
  refreshToken?: string;
  createdAt?: string | null;
  updatedAt?: string | null;
};
export type UpdateUserDto = {
  id: number;
  name?: string;
  phone?: string;
  address?: string;
  email?: string;
  password?: string;
  role?: number;
  companyId?: number;
  refreshToken?: string;
  createdAt?: string | null;
  updatedAt?: string | null;
};
export type ChangePasswordDto = {
  id: number;
  oldPassword: string;
  newPassword: string;
  oldEmail: string;
  newEmail: string;
};
export type CreateCompanyDto = {
  id?: number;
  companyName: string;
};
export type UpdateCompanyDto = {
  id: number;
  companyName?: string;
};
export type CreateDemoDto = {
  id?: number;
  firmware?: string | null;
  inventoryHardwareId?: number | null;
  companyId: number;
  contactName?: string | null;
  softwareId?: number | null;
  softwareVersionId?: number | null;
  shipDate?: string | null;
  startDate?: string | null;
  endDate?: string | null;
};
export type HardwareTypeLink = {
  id: number;
  name: string;
  link: string;
  hardwareTypeId: number;
  createdAt: string | null;
  updatedAt: string | null;
  hardwareType: HardwareType;
};
export type InventoryPartUsed = {
  id: number;
  partName: string;
  amount: number;
  rmaId: number;
  rma: Rma;
};
export type RepairComment = {
  id: number;
  commentBody: string;
  rmaId: number;
  dateCreated: string;
  rma: Rma;
};
export type Rma = {
  id: number;
  nameId: string;
  companyId: number;
  company: Company;
  contactName: string;
  email: string;
  phoneNumber: string | null;
  hardwareId: number;
  hardware: Hardware;
  issue: string | null;
  status: string | null;
  notes: string | null;
  solution: string | null;
  shipDate: string | null;
  repairPrice: string | null;
  trackingNumber: string | null;
  shipper: string | null;
  creationDate: string | null;
  inventoryPartsUsed: InventoryPartUsed[];
  repairComments: RepairComment[];
  isComplete: boolean;
  isRepaired: boolean;
  orderId: number | null;
  order: Order;
};
export type SoftwareLink = {
  id: number;
  name: string;
  link: string;
  softwareId: number;
  software: Software;
  createdAt: string | null;
  updatedAt: string | null;
};
export type Department = {
  id: number;
  departmentName: string;
  companyId: number;
  company: Company;
  quoteLink: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  lastContacted: string;
  expirationDate: string;
  hardware: Hardware[];
};
export type SupportPurchase = {
  id: number;
  orderId: number | null;
  order: Order;
  softwareId: number;
  software: Software;
  companyId: number | null;
  company: Company;
  supportStartDate: string;
  supportEndDate: string;
  dateCreated: string;
  hardware: Hardware[];
};
export type OperatingSystem = {
  id: number;
  osName: string;
  softwareVersions: SoftwareVersion[];
};
export type SoftwareVersion = {
  id: number;
  versionType: string;
  versionNumber: string;
  link: string;
  changelog?: string | null;
  size: number;
  softwareId: number;
  operatingSystemsId: number;
  userguideName?: string | null;
  userguideLink?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  demos?: Demo[];
  operatingSystem: OperatingSystem;
  software: Software;
};
export type Software = {
  id: number;
  type: string;
  softwareLinks: SoftwareLink[];
  licenses: License[];
  supportPurchases: SupportPurchase[];
  softwareVersions: SoftwareVersion[];
  demos: Demo[];
};
export type Module = {
  id: number;
  moduleName: string;
  licenses: License[];
};
export type License = {
  id: number;
  license: string;
  softwareId: number;
  software: Software;
  hardwareId: number;
  hardware: Hardware;
  moduleId: number | null;
  module: Module | null;
  companyId: number;
  company: Company;
  orderId: number | null;
  order: Order | null;
  notes: string | null;
};
export type Order = {
  id: number;
  companyId: number;
  company: Company;
  purchaseOrderLink: string | null;
  purchaseOrderName: string | null;
  quoteLink: string | null;
  quoteName: string | null;
  orderDate: string;
  price: string;
  completed: boolean;
  hardware: Hardware[];
  rmas: Rma[];
  licenses: License[];
  supportPurchases: SupportPurchase[];
  notes: string | null;
};
export type SystemModuleLicense = {
  id: number;
  module: string;
  license: string;
  iv: string;
  companyId: number;
  company: Company;
  orderId: number | null;
};
export type User = {
  id: number;
  name: string;
  phone: string;
  address: string;
  email: string;
  password: string;
  role: number;
  companyId: number;
  company: Company;
  refreshToken: string;
  createdAt: string | null;
  updatedAt: string | null;
};
export type Company = {
  id: number;
  companyName: string;
  hardware: Hardware[];
  orders: Order[];
  demos: Demo[];
  licenses: License[];
  systemModuleLicenses: SystemModuleLicense[];
  supportPurchases: SupportPurchase[];
  users: User[];
  rmas: Rma[];
  departments: Department[];
};
export type Hardware = {
  id: number;
  serial: string;
  companies: Company[];
  hardwareTypeId: number;
  hardwareType: HardwareType;
  rmas: Rma[];
  licenses: License[];
  firmware: string | null;
  notes: string | null;
  manufacturer: string | null;
  warrantyStartDate: string | null;
  warrantyEndDate: string | null;
  trackingNumber: string | null;
  shipper: string | null;
  lptSold: boolean;
  orderId: number | null;
  order: Order;
  supportPurchases: SupportPurchase[];
  departmentId: number | null;
  department: Department | null;
};
export type Firmware = {
  id: number;
  name: string;
  link: string;
  changelog: string;
  size: number;
  hardwareTypeId: number;
  hardwareType: HardwareType;
  userguideName: string;
  userguideLink: string;
  createdAt: string | null;
  updatedAt: string | null;
};
export type HardwareCategory = {
  id: number;
  categoryName: string;
  hardwareTypes: HardwareType[];
};
export type HardwareType = {
  id: number;
  typeName: string;
  hardwareTypeLinks: HardwareTypeLink[];
  hardware: Hardware[];
  inventoryHardware: InventoryHardware[];
  firmware: Firmware[];
  hardwareCategoryId: number;
  hardwareCategory: HardwareCategory;
};
export type InventoryHardware = {
  id: number;
  serial: string;
  hardwareTypeId: number;
  hardwareType: HardwareType;
  demo?: Demo;
  macAddress: string | null;
  dateReceived: string | null;
};
export type Demo = {
  id: number;
  firmware: string | null;
  inventoryHardwareId: number | null;
  inventoryHardware?: InventoryHardware;
  companyId: number;
  company: Company;
  contactName: string | null;
  softwareId: number | null;
  software?: Software;
  softwareVersionId: number | null;
  softwareVersion?: SoftwareVersion;
  shipDate: string | null;
  startDate: string | null;
  endDate: string | null;
};
export type UpdateDemoDto = {
  id: number;
  firmware?: string | null;
  inventoryHardwareId?: number | null;
  companyId?: number;
  contactName?: string | null;
  softwareId?: number | null;
  softwareVersionId?: number | null;
  shipDate?: string | null;
  startDate?: string | null;
  endDate?: string | null;
};
export type CreateSoftwareDto = {
  id?: number;
  type: string;
};
export type UpdateSoftwareDto = {
  id: number;
  type?: string;
};
export type CreateSoftwareVersionDto = {
  id?: number;
  versionType?: string;
  versionNumber: string;
  link: string;
  changelog?: string;
  size: number;
  softwareId: number;
  operatingSystemsId: number;
  userguideName?: string;
  userguideLink?: string;
};
export type UpdateSoftwareVersionDto = {
  id: number;
  versionType?: string | null;
  versionNumber?: string;
  link?: string;
  changelog?: string | null;
  size?: number;
  softwareId?: number;
  operatingSystemsId?: number;
  userguideName?: string | null;
  userguideLink?: string | null;
};
export type SendVersionReleaseEmailDto = {
  message: string;
  softwareId: number;
  versionNumber: string;
};
export type CreateOperatingSystemDto = {
  id?: number;
  osName: string;
};
export type UpdateOperatingSystemDto = {
  id: number;
  osName?: string;
};
export type CreateLicenseDto = {
  id?: number;
  license: string;
  softwareId: number;
  companyId: number;
  hardwareId: number;
  orderId?: number | null;
  moduleId?: number | null;
  notes?: string | null;
};
export type UpdateLicenseDto = {
  id: number;
  license?: string;
  softwareId?: number;
  companyId?: number;
  hardwareId?: number;
  orderId?: number | null;
  moduleId?: number | null;
  notes?: string | null;
};
export type ParseLicenseDto = {
  licenses: object[];
};
export type CreateHardwareDto = {
  id?: number;
  serial: string;
  companies: Company[];
  hardwareTypeId: number;
  firmware?: string;
  manufacturer?: string | null;
  notes?: string | null;
  warrantyStartDate?: string | null;
  warrantyEndDate?: string | null;
  trackingNumber?: string | null;
  shipper?: string | null;
  lptSold: boolean;
  orderId?: number | null;
  departmentId?: number | null;
};
export type UpdateHardwareDto = {
  id: number;
  serial?: string;
  companies?: Company[];
  hardwareTypeId?: number;
  firmware?: string | null;
  manufacturer?: string | null;
  notes?: string | null;
  warrantyStartDate?: string | null;
  warrantyEndDate?: string | null;
  trackingNumber?: string | null;
  shipper?: string | null;
  lptSold?: boolean;
  orderId?: number | null;
  departmentId?: number | null;
};
export type CreateHardwareTypeDto = {
  id?: number;
  typeName: string;
  hardwareCategoryId: number;
};
export type UpdateHardwareTypeDto = {
  id: number;
  typeName?: string;
  hardwareCategoryId?: number;
};
export type CreateHardwareCategoryDto = {
  id?: number;
  categoryName: string;
};
export type UpdateHardwareCategoryDto = {
  id: number;
  categoryName?: string;
};
export type CreateModuleDto = {
  id?: number;
  moduleName: string;
};
export type UpdateModuleDto = {
  id: number;
  moduleName?: string;
};
export type CreateOrderDto = {
  id?: number;
  companyId: number;
  purchaseOrderLink?: string;
  purchaseOrderName?: string;
  quoteLink?: string | null;
  quoteName?: string | null;
  orderDate: string;
  price: string;
  completed?: boolean;
  notes?: string | null;
};
export type UpdateOrderDto = {
  id: number;
  companyId?: number;
  purchaseOrderLink?: string | null;
  purchaseOrderName?: string | null;
  quoteLink?: string | null;
  quoteName?: string | null;
  orderDate?: string;
  price?: string;
  completed?: boolean;
  notes?: string | null;
};
export type CreateInventoryHardwareDto = {
  id?: number;
  serial: string;
  macAddress?: string | null;
  hardwareTypeId: number;
  dateReceived?: string | null;
};
export type UpdateInventoryHardwareDto = {
  id: number;
  serial?: string;
  hardwareTypeId?: number;
  macAddress?: string;
  dateReceived?: string | null;
};
export type CreateFirmwareDto = {
  id?: number;
  name: string;
  link: string;
  changelog?: string;
  size?: number;
  hardwareTypeId: number;
  userguideName: string;
  userguideLink: string;
  createdAt?: string;
  updatedAt?: string;
};
export type UpdateFirmwareDto = {
  id: number;
  name?: string;
  link?: string;
  changelog?: string | null;
  size?: number | null;
  hardwareTypeId?: number;
  userguideName?: string;
  userguideLink?: string;
  createdAt?: string | null;
  updatedAt?: string | null;
};
export type CreateHardwareTypeLinkDto = {
  id?: number;
  name: string;
  link: string;
  hardwareTypeId: number;
};
export type UpdateHardwareTypeLinkDto = {
  id: number;
  name?: string;
  link?: string;
  hardwareTypeId?: number;
  createdAt?: string | null;
  updatedAt?: string | null;
};
export type InventoryPart = {
  id: number;
  partName: string;
  amount: number;
};
export type CreateInventoryPartDto = {
  id?: number;
  partName: string;
  amount: number;
};
export type UpdateInventoryPartDto = {
  id: number;
  partName?: string;
  amount?: number;
};
export type CreateInventoryPartsUsedDto = {
  id?: number;
  partName: string;
  amount: number;
  rmaId: number;
};
export type UpdateInventoryPartsUsedDto = {
  id: number;
  partName?: string;
  amount?: number;
  rmaId?: number;
};
export type CreateRmaDto = {
  id?: number;
  nameId: string;
  companyId: number;
  contactName?: string;
  email?: string;
  phoneNumber?: string;
  hardwareId: number;
  inventoryPartsUsed?: CreateInventoryPartsUsedDto[];
  issue?: string;
  status?: string;
  notes?: string;
  solution?: string;
  shipDate?: string;
  repairPrice?: string;
  trackingNumber?: string;
  shipper?: string;
  creationDate?: string;
  isComplete?: boolean;
  isRepaired?: boolean;
  orderId?: number | null;
};
export type SendEmailDto = {
  emailText: string;
  emailAddress: string;
  rmaNameId: string;
};
export type SendDepartmentEmailDto = {
  message: string;
};
export type UpdateRmaDto = {
  id: number;
  nameId?: string;
  companyId?: number;
  contactName?: string;
  email?: string;
  phoneNumber?: string;
  hardwareId?: number;
  issue?: string;
  status?: string;
  inventoryPartsUsed?: CreateInventoryPartsUsedDto[];
  notes?: string;
  solution?: string;
  shipDate?: string;
  repairPrice?: string;
  trackingNumber?: string;
  shipper?: string;
  isComplete?: boolean;
  isRepaired?: boolean;
  creationDate?: string;
  orderId?: number | null;
};
export type CreatePasswordResetDto = {
  email: string;
};
export type ProcessResetDto = {
  token: string;
  password: string;
};
export type CreateRepairCommentDto = {
  id?: number;
  commentBody: string;
  rmaId: number;
  dateCreated?: string;
};
export type UpdateRepairCommentDto = {
  id: number;
  commentBody?: string;
  rmaId?: number;
  dateCreated?: string;
};
export type CreateSignupDto = {
  id?: number;
  name: string;
  phone: string;
  address?: string;
  email: string;
  password: string;
  analyzerSerial?: string;
  company: string;
};
export type Signup = {
  id: number;
  name: string;
  phone: string;
  address: string;
  email: string;
  password: string;
  analyzerSerial: string;
  company: string;
};
export type UpdateSignupDto = {
  id: number;
  name?: string;
  phone?: string;
  address?: string;
  email?: string;
  password?: string;
  analyzerSerial?: string;
  company?: string;
};
export type CreateSoftwareLinkDto = {
  id?: number;
  name: string;
  link: string;
  softwareId: number;
  createdAt?: string | null;
  updatedAt?: string | null;
};
export type UpdateSoftwareLinkDto = {
  id: number;
  name?: string;
  link?: string;
  softwareId?: number;
  createdAt?: string | null;
  updatedAt?: string | null;
};
export type CreateSupportPurchaseDto = {
  id?: number;
  orderId?: number | null;
  softwareId: number;
  companyId: number;
  supportStartDate: string;
  supportEndDate: string;
  dateCreated?: string;
  hardware: Hardware[];
};
export type UpdateSupportPurchaseDto = {
  id: number;
  orderId?: number | null;
  softwareId?: number;
  companyId?: number;
  supportStartDate?: string;
  supportEndDate?: string;
  dateCreated?: string;
  hardware?: Hardware[];
};
export type Utility = {
  id: number;
  name: string;
  link: string;
  createdAt: string | null;
  updatedAt: string | null;
};
export type CreateUtilityDto = {
  id?: number;
  name: string;
  link: string;
  createdAt?: string;
  updatedAt?: string;
};
export type UpdateUtilityDto = {
  id: number;
  name?: string;
  link?: string;
  createdAt?: string;
  updatedAt?: string;
};
export type CreateDepartmentDto = {
  id?: number;
  departmentName: string;
  companyId: number;
  quoteLink?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  lastContacted?: string;
  expirationDate?: string;
  hardware?: Hardware[];
};
export type UpdateDepartmentDto = {
  id: number;
  departmentName?: string;
  companyId?: number;
  quoteLink?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  lastContacted?: string | null;
  expirationDate?: string | null;
  hardware?: Hardware[];
};
export type CreateSystemModuleLicenseDto = {
  id?: number;
  license: string;
  module: string;
  iv: string;
  companyId: number;
  orderId?: number | null;
};
export type UpdateSystemModuleLicenseDto = {
  id?: number;
  license?: string;
  module?: string;
  iv?: string;
  companyId?: number;
  orderId?: number | null;
};
export type ParseSystemModuleLicenseDto = {
  companyId: number;
  licenses: object[];
};
